import { globalAny } from "../../utils/Utils";
import ComponentThemeType from "../../models/ComponentTheme";
import TouchableButton from "../TouchableButton";
import PrivacyPolicyButton from "../PrivacyPolicyButton";
import {
	StyledButtonContainer,
	StyledLegalTitleStyle,
	StyledNarrowBodyContent,
	StyledNarrowButton,
	StyledNarrowContent,
	StyledNarrowCreateButton,
	StyledNarrowMainContent,
	StyledNarrowTextContent,
	StyledWideBodyContent,
	StyledWideContent,
	StyledWideFooter,
	StyledWideTextContent,
} from "./styled";
import useGenericContentStore from "../../store/genericContent.store";
import { Props } from "./model";
import WideCreateAccountWrapper from "../Layout/WideCreateAccountWrapper";
import NarrowCreateAccountWrapper from "../Layout/NarrowCreateAccountWrapper";
import { TouchableButtonDataProps } from "../../models/TouchableButtonDataProps";
import ComponentTypeEnum from "../../models/ComponentTypeEnum";

const LegalData = (props: Props) => {
	const { isNarrow } = useGenericContentStore((state) => ({ isNarrow: state.isNarrow }));

	const touchableButtonData: TouchableButtonDataProps[] = [
		{
			title: globalAny.language.next,
			type: ComponentTypeEnum.Primary,
			disabled: false,
			leftText: isNarrow,
			onPress: props.onPressNext,
		},
	];

	/**
	 * Renders a reusable `TouchableButton` component for legal/accept data actions.
	 *
	 * @param {Object} props - Button properties.
	 * @param {string} props.title - Text displayed on the button.
	 * @param {string} props.type - Button type (e.g., primary, secondary).
	 * @param {boolean} props.leftText - Whether to align text to the left.
	 * @param {boolean} props.disabled - Whether the button is disabled.
	 * @param {Function} props.onPress - Callback function executed on button press.
	 *
	 * @returns JSX.Element - A styled TouchableButton component.
	 */
	const renderTouchableButtonData = ({ title, type, leftText, disabled, onPress }: any) => (
		<TouchableButton
			key={`legal-data-touchable-${title}`}
			leftText={leftText}
			componentTheme={ComponentThemeType.VinFast}
			type={type}
			onPress={onPress}
			disabled={disabled}
		>
			{title}
		</TouchableButton>
	);

	/**
	 * Renders the wide-screen layout for the Legal Data component.
	 *
	 * - Displays the legal title and body text.
	 * - Optionally renders action buttons and the Privacy Policy button
	 *   if `props.showButton` is true.
	 *
	 * @returns JSX.Element - The wide-screen layout JSX.
	 */
	const renderWideScreen = () => (
		<WideCreateAccountWrapper>
			<StyledWideContent>
				<StyledWideTextContent>
					<StyledLegalTitleStyle isNarrow={isNarrow}>{globalAny.language.legal_data}</StyledLegalTitleStyle>
					<StyledWideBodyContent>{props.bodyText}</StyledWideBodyContent>
				</StyledWideTextContent>
			</StyledWideContent>

			{props.showButton && (
				<StyledWideFooter>
					<StyledButtonContainer>
						{touchableButtonData.map((item) => {
							return renderTouchableButtonData(item);
						})}
					</StyledButtonContainer>
					<PrivacyPolicyButton onPress={props.onPressPrivacy} />
				</StyledWideFooter>
			)}
		</WideCreateAccountWrapper>
	);

	/**
	 * Renders the narrow-screen layout for the Legal Data component.
	 *
	 * - Optionally displays action buttons and the Privacy Policy button
	 *   if `props.showButton` is true.
	 * - Displays the legal title and body text in a compact vertical layout.
	 *
	 * @returns JSX.Element - The narrow-screen layout JSX.
	 */
	const renderNarrowScreen = () => (
		<NarrowCreateAccountWrapper>
			{props.showButton && (
				<StyledNarrowButton>
					<StyledNarrowCreateButton>
						{touchableButtonData.map((item) => {
							return renderTouchableButtonData(item);
						})}
					</StyledNarrowCreateButton>
					<PrivacyPolicyButton onPress={props.onPressPrivacy} />
				</StyledNarrowButton>
			)}

			<StyledNarrowMainContent>
				<StyledNarrowContent>
					<StyledNarrowTextContent>
						<StyledLegalTitleStyle isNarrow={isNarrow}>{globalAny.language.legal_data}</StyledLegalTitleStyle>
						<StyledNarrowBodyContent>{props.bodyText}</StyledNarrowBodyContent>
					</StyledNarrowTextContent>
				</StyledNarrowContent>
			</StyledNarrowMainContent>
		</NarrowCreateAccountWrapper>
	);
	return isNarrow ? renderNarrowScreen() : renderWideScreen();
};

export default LegalData;
